import type { SizingMode } from '@/lib/types'

/**
 * Dimensionnement posé sous le graphique : lot, risque en devise, ou risque
 * en pourcentage du capital.
 *
 * Le sélecteur est là parce que c'est là qu'on décide. Descendre au ticket
 * pour passer de « 0,386 lot » à « 1 % » casse le geste au moment précis où
 * l'on regarde le prix.
 *
 * Les incréments suivent chacun leur unité :
 *
 * - **lot** : le pas RÉEL du courtier (`stepVolume`), jamais une valeur
 *   arbitraire — proposer 0,05 là où le courtier n'accepte que des multiples
 *   de 0,01 produirait des volumes réajustés sans qu'on comprenne pourquoi.
 *   L'arithmétique se fait en nombre de pas, en entiers : en virgule
 *   flottante, `0.1 + 0.01` vaut 0.11000000000000001 et le volume finirait
 *   par ne plus tomber sur le pas.
 * - **devise** : une unité, et dix pour le bouton rapide.
 * - **pourcentage** : un dixième de point, et un point.
 */
export function SizingControl({
  mode,
  onChangeMode,
  lots,
  onChangeLots,
  amount,
  onChangeAmount,
  percent,
  onChangePercent,
  step,
  min,
  max,
  realRisk,
  currency,
}: {
  mode: SizingMode
  onChangeMode: (mode: SizingMode) => void
  /** Volume : saisi en mode `lots`, recalculé sinon. */
  lots: string
  onChangeLots: (lots: string) => void
  amount: string
  onChangeAmount: (amount: string) => void
  percent: string
  onChangePercent: (percent: string) => void
  /** Bornes du courtier, pour le mode lot. */
  step: string
  min: string
  max: string
  /** Perte réelle possible, rappelée sous la valeur quel que soit le mode. */
  realRisk: string | null
  currency: string
}) {
  const stepValue = Number(step) || 0.01
  const minValue = Number(min) || stepValue
  const maxValue = Number(max) || Number.MAX_SAFE_INTEGER
  // Décimales imposées par le pas : 0.01 -> 2, 0.1 -> 1.
  const lotDecimals = Math.max(0, (step.split('.')[1] ?? '').replace(/0+$/, '').length)

  /** Réglages propres à chaque mode : valeur, unité, pas, bornes. */
  const settings = {
    lots: {
      value: lots,
      unit: 'lot',
      decimals: lotDecimals,
      small: stepValue,
      large: stepValue * 10,
      floor: minValue,
      ceiling: maxValue,
      apply: onChangeLots,
    },
    amount: {
      value: amount,
      unit: currency,
      decimals: 2,
      small: 1,
      large: 10,
      floor: 1,
      ceiling: Number.MAX_SAFE_INTEGER,
      apply: onChangeAmount,
    },
    percent: {
      value: percent,
      unit: '%',
      decimals: 2,
      small: 0.1,
      large: 1,
      floor: 0.1,
      ceiling: 100,
      apply: onChangePercent,
    },
  }[mode]

  function shift(times: number, size: number) {
    const current = Number(settings.value) || 0
    // Arithmétique en nombre de pas, en entiers : voir la note du composant.
    const steps = Math.round(current / size) + times
    const next = Math.min(Math.max(steps * size, settings.floor), settings.ceiling)
    settings.apply(next.toFixed(settings.decimals))
  }

  const current = Number(settings.value) || 0

  return (
    /*
      Une seule ligne, pleine largeur sur téléphone.
      ------------------------------------------------------------------
      À sa largeur naturelle, l'ensemble demandait une vingtaine de pixels
      de plus que les 375 disponibles : le bouton de grand pas basculait
      seul sur une deuxième ligne, et cette ligne presque vide poussait le
      reste de l'écran vers le bas pour rien. Les éléments gardent leur
      taille, c'est le bloc central qui absorbe ce qui reste.
    */
    <div className="flex w-full items-center gap-1 rounded-md border border-border bg-elevated/60 p-1 md:w-auto">
      {/* Choix de l'unité de raisonnement. */}
      <div className="flex shrink-0 overflow-hidden rounded border border-border">
        {(
          [
            { key: 'lots', label: 'lot' },
            { key: 'amount', label: currency },
            { key: 'percent', label: '%' },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onChangeMode(item.key)}
            aria-pressed={mode === item.key}
            className={`num h-touch px-1.5 text-[11px] font-semibold transition-colors sm:px-2 ${
              mode === item.key
                ? 'bg-accent text-white'
                : 'bg-panel text-ink-muted hover:bg-border/60'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => shift(-1, settings.small)}
        disabled={current <= settings.floor}
        aria-label="Diminuer"
        className="num h-touch w-9 shrink-0 rounded bg-elevated text-[16px] font-bold text-ink hover:bg-border/60 active:bg-border disabled:opacity-30"
      >
        −
      </button>

      <div className="min-w-0 flex-1 px-1 text-center md:min-w-[86px] md:flex-none">
        <p className="num text-[13px] font-semibold text-ink-strong">
          {current.toFixed(settings.decimals)}
          <span className="ml-1 text-[10px] font-normal text-ink-muted">{settings.unit}</span>
        </p>
        {/*
          La perte RÉELLE, rappelée quel que soit le mode. En mode devise ou
          pourcentage, le chiffre saisi porte sur MON stop : sans ce rappel, on
          croirait risquer le montant tapé alors que l'ordre part inversé.
        */}
        {realRisk && (
          <p className="num text-[10px] text-down">
            −{realRisk} {currency}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => shift(1, settings.small)}
        disabled={current >= settings.ceiling}
        aria-label="Augmenter"
        className="num h-touch w-9 shrink-0 rounded bg-elevated text-[16px] font-bold text-ink hover:bg-border/60 active:bg-border disabled:opacity-30"
      >
        +
      </button>

      {/*
        Grand pas : passer de 0,10 à 1,00 lot demanderait 90 appuis. Le
        bouton affiche l'incrément réel plutôt qu'un « ++ » : il ne vaut pas
        la même chose selon l'unité — 0,10 lot, 10 EUR, ou 1 %.
      */}
      <button
        type="button"
        onClick={() => shift(1, settings.large)}
        aria-label={`Augmenter de ${settings.large} ${settings.unit}`}
        className="num h-touch min-w-[38px] shrink-0 rounded bg-elevated px-1 text-[10px] text-ink-muted hover:bg-border/60 active:bg-border"
      >
        +{settings.large.toFixed(settings.large < 1 ? settings.decimals : 0)}
      </button>
    </div>
  )
}
